import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import dayjs from 'dayjs';
import { AthleteEvent, AthletePackageRetrieve } from 'types';

import { ALL_CHANGE_STEPS, ChangeStep } from './changeModalSteps';
import { canAdvanceFromStep } from './changeStepValidators';
import useChangeForm, { CapturedChangeConfig, ChangeForm } from './hooks/useChangeForm';
import useChangeSubmission, { ChangeMutation } from './hooks/useChangeSubmission';
import useWizardNavigation from './hooks/useWizardNavigation';
import useUpcomingReservationsSummary from '../CancelModal/hooks/useUpcomingReservationsSummary';

export type ChangePhase = 'timing' | 'capturing' | 'finalizing';

interface ChangeModalContextType {
  athletePackage: AthletePackageRetrieve;
  isDesktop: boolean;
  form: ChangeForm;

  phase: ChangePhase;
  // Drawer open helpers
  isDrawerOpen: boolean;
  isModalOpen: boolean;
  lockedStartDate: Date | null;
  handleCapture: (captured: CapturedChangeConfig) => void;
  handleDrawerCancel: () => void;

  currentStep: ChangeStep;
  visibleSteps: ChangeStep[];
  goNext: () => void;
  goBack: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;

  upcomingReservations: AthleteEvent[];
  hasUpcomingReservations: boolean;
  upcomingCreditedCount: number;
  upcomingUncreditedCount: number;
  upcomingTotalCount: number;
  isLoadingReservations: boolean;

  changeMutation: ChangeMutation;
  submit: () => void;
  errorMessage: string | null;

  handleClose: () => void;
}

const ChangeModalContext = createContext<ChangeModalContextType | null>(null);

export const useChangeModal = (): ChangeModalContextType => {
  const ctx = useContext(ChangeModalContext);
  if (!ctx) {
    throw new Error('useChangeModal must be used within ChangeModalProvider');
  }
  return ctx;
};

interface ChangeModalProviderProps {
  athletePackage: AthletePackageRetrieve;
  isDesktop: boolean;
  closeHandler: () => void;
  changeMutation: ChangeMutation;
  opened: boolean;
  children: React.ReactNode;
}

export const ChangeModalProvider: React.FC<ChangeModalProviderProps> = ({
  athletePackage,
  isDesktop,
  closeHandler,
  changeMutation,
  opened,
  children,
}) => {
  const form = useChangeForm();
  const [phase, setPhase] = useState<ChangePhase>('timing');

  // Reservations query target date depends on timing.
  const cancelNowForQuery = form.values.timing === 'TOMORROW';
  const cancellationDateForQuery: Date | null =
    form.values.timing === 'WHEN_ENDS' && athletePackage.endDate ? new Date(athletePackage.endDate) : null;

  const reservations = useUpcomingReservationsSummary(
    athletePackage.athletePackageId,
    cancellationDateForQuery,
    cancelNowForQuery,
  );

  const visibleSteps = useMemo<ChangeStep[]>(
    () =>
      ALL_CHANGE_STEPS.filter(step =>
        step === 'Reservation Handling' ? reservations.hasUpcomingReservations : true,
      ),
    [reservations.hasUpcomingReservations],
  );

  // Wizard navigation. We override goNext for the Timing step so it transitions to the
  // capturing phase instead of advancing the in-modal wizard.
  const navigation = useWizardNavigation({
    visibleSteps,
    initialStep: 'Timing',
    opened,
    beforeAdvance: step => canAdvanceFromStep(step, form),
  });

  // The drawer's start date is locked to the date implied by the chosen timing.
  const lockedStartDate = useMemo<Date | null>(() => {
    if (form.values.timing === 'TOMORROW') {
      return dayjs().add(1, 'day').toDate();
    }
    if (form.values.timing === 'WHEN_ENDS' && athletePackage.endDate) {
      return dayjs(athletePackage.endDate).add(1, 'day').toDate();
    }
    return null;
  }, [form.values.timing, athletePackage.endDate]);

  const handleCapture = useCallback(
    (captured: CapturedChangeConfig) => {
      form.setFieldValue('captured', captured);
      // Advance to the step after Timing here (not when entering 'capturing') so the modal's
      // close animation keeps showing Timing instead of flashing the next step.
      navigation.setCurrentStep(reservations.hasUpcomingReservations ? 'Reservation Handling' : 'Review');
      setPhase('finalizing');
    },
    [form, navigation, reservations.hasUpcomingReservations],
  );

  const handleDrawerCancel = useCallback(() => {
    setPhase('timing');
    // Snap the wizard back to Timing so the modal renders that step when it reopens.
    navigation.setCurrentStep('Timing');
  }, [navigation]);

  // Wrap goNext to handle the Timing → capturing transition. The wizard is not advanced here;
  // handleCapture advances it once the drawer captures, so the closing modal keeps showing Timing.
  const goNext = useCallback(() => {
    if (navigation.currentStep === 'Timing') {
      if (reservations.isLoading) return;
      if (!canAdvanceFromStep('Timing', form)) return;
      setPhase('capturing');
      return;
    }
    navigation.goNext();
  }, [navigation, form, reservations.isLoading]);

  // Wrap goBack to handle Reservation Handling → capturing (reopen drawer).
  const goBack = useCallback(() => {
    if (navigation.currentStep === 'Reservation Handling') {
      setPhase('capturing');
      return;
    }
    navigation.goBack();
  }, [navigation]);

  const submission = useChangeSubmission({
    values: form.values,
    changeMutation,
    onSuccess: closeHandler,
  });

  // Full reset whenever the modal closes externally.
  useEffect(() => {
    if (!opened) {
      form.reset();
      setPhase('timing');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const { resetError } = submission;

  const handleClose = useCallback(() => {
    resetError();
    closeHandler();
  }, [resetError, closeHandler]);

  // Derived booleans so the shell doesn't need to know about phase directly.
  const isDrawerOpen = phase === 'capturing' && opened;
  const isModalOpen = opened && phase !== 'capturing';
  const isFirstStep =
    navigation.currentStep === 'Timing' || visibleSteps.indexOf(navigation.currentStep) === 0;
  const isLastStep = navigation.currentStep === 'Review';

  const value: ChangeModalContextType = {
    athletePackage,
    isDesktop,
    form,
    phase,
    isDrawerOpen,
    isModalOpen,
    lockedStartDate,
    handleCapture,
    handleDrawerCancel,
    currentStep: navigation.currentStep,
    visibleSteps,
    goNext,
    goBack,
    isFirstStep,
    isLastStep,
    ...reservations,
    isLoadingReservations: reservations.isLoading,
    changeMutation,
    submit: submission.submit,
    errorMessage: submission.errorMessage,
    handleClose,
  };

  return <ChangeModalContext.Provider value={value}>{children}</ChangeModalContext.Provider>;
};
